import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AnalyticsManager from '../../../../app/analytics';
import * as routes from '../../../../constants/routes';
import {
  AUTOCOMPLETE_STATUS,
  getAutocompleteResults,
  setAutocompleteResults,
  setHomepageSearchQuery,
  searchBenefits,
} from '../../../../redux/actions';

const IDLE_SEARCH_DELAY = 500;

export const PREVIEW_STATUS = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  RESULTS: 'RESULTS',
  NO_RESULTS: 'NO_RESULTS',
};

export const SEARCH_DISPLAY_STATUS = {
  MINIMUM_LENGTH: 'MINIMUM_LENGTH',
  LOADING: 'LOADING',
  NO_RESULTS: 'NO_RESULTS',
};

const extractPreviewRecords = (response) => (
  response?.benefits?.records ?? response?.records ?? []
);

const canScheduleIdleSearch = (query) => query.length >= 3;

export const useSearchAutocomplete = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const results = useSelector((store) => store.data.autocompleteResults);
  const autocompleteStatus = useSelector((store) => store.data.autocompleteStatus);
  const searchQuery = useSelector((store) => store.data.homepageSearchQuery);

  const [isFocused, setIsFocused] = useState(false);
  const [typedQuery, setTypedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [previewBenefits, setPreviewBenefits] = useState([]);
  const [previewStatus, setPreviewStatus] = useState(PREVIEW_STATUS.IDLE);

  const inputRef = useRef();
  const resultRef = useRef();
  const idleSearchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const previewQueryRef = useRef('');

  const latestRef = useRef({});
  latestRef.current = { typedQuery, selectedIndex, results, autocompleteStatus };

  const setInputQuery = useCallback((query) => {
    dispatch(setHomepageSearchQuery(query));
  }, [dispatch]);

  const getMatchQuery = useCallback(() => (
    selectedIndex >= 0 ? typedQuery : searchQuery
  ), [selectedIndex, typedQuery, searchQuery]);

  const matchQuery = getMatchQuery();

  const searchDisplayStatus = useMemo(() => {
    if ((matchQuery || '').trim().length < 3) return SEARCH_DISPLAY_STATUS.MINIMUM_LENGTH;

    const hasAutocompleteResults = autocompleteStatus === AUTOCOMPLETE_STATUS.RESULTS && results.length > 0;
    const hasPreviewResults = previewStatus === PREVIEW_STATUS.RESULTS && previewBenefits.length > 0;

    if (hasAutocompleteResults || hasPreviewResults) return null;

    const autocompleteDone = autocompleteStatus === AUTOCOMPLETE_STATUS.NO_RESULTS
      || autocompleteStatus === AUTOCOMPLETE_STATUS.ERROR;
    const previewDone = previewStatus === PREVIEW_STATUS.NO_RESULTS;

    if (autocompleteDone && previewDone) {
      return SEARCH_DISPLAY_STATUS.NO_RESULTS;
    }

    if (
      autocompleteStatus === AUTOCOMPLETE_STATUS.LOADING
      || previewStatus === PREVIEW_STATUS.LOADING
      || (autocompleteDone && previewStatus === PREVIEW_STATUS.IDLE)
    ) {
      return SEARCH_DISPLAY_STATUS.LOADING;
    }

    return null;
  }, [matchQuery, autocompleteStatus, previewStatus, results, previewBenefits]);

  const clearPreview = useCallback(() => {
    previewQueryRef.current = '';
    setPreviewBenefits([]);
    setPreviewStatus(PREVIEW_STATUS.IDLE);
  }, []);

  const clearIdleSearch = useCallback(() => {
    clearTimeout(idleSearchTimeoutRef.current);
    idleSearchTimeoutRef.current = null;
  }, []);

  const resetAutocompleteRequest = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const fetchAutocomplete = useCallback((query) => {
    resetAutocompleteRequest();
    abortControllerRef.current = new AbortController();
    AnalyticsManager.logQuickSearch(query);
    dispatch(getAutocompleteResults(
      query,
      () => {},
      abortControllerRef.current.signal,
    ));
  }, [dispatch, resetAutocompleteRequest]);

  const fetchSearchPreview = useCallback((query) => {
    previewQueryRef.current = query;
    setPreviewStatus(PREVIEW_STATUS.LOADING);
    setPreviewBenefits([]);
    AnalyticsManager.logQuickSearch(query);
    dispatch(searchBenefits({ query, page: 1, size: 20 }, (response) => {
      if (previewQueryRef.current !== query) return;
      const records = extractPreviewRecords(response);
      setPreviewBenefits(records);
      setPreviewStatus(
        records.length > 0 ? PREVIEW_STATUS.RESULTS : PREVIEW_STATUS.NO_RESULTS,
      );
    }));
  }, [dispatch]);

  const scheduleIdleSearch = useCallback((query) => {
    clearIdleSearch();
    if (!canScheduleIdleSearch(query)) return;

    idleSearchTimeoutRef.current = setTimeout(() => {
      const { typedQuery: currentTypedQuery, selectedIndex: currentIndex, results: currentResults } = latestRef.current;
      if (!canScheduleIdleSearch(currentTypedQuery)) return;

      const searchTarget = currentIndex >= 0
        ? currentResults[currentIndex]
        : currentTypedQuery;
      if (!searchTarget) return;

      fetchSearchPreview(searchTarget);
    }, IDLE_SEARCH_DELAY);
  }, [clearIdleSearch, fetchSearchPreview]);

  const navigateSelection = useCallback((nextIndex) => {
    const { typedQuery: currentTypedQuery, selectedIndex: currentIndex, results: currentResults } = latestRef.current;
    let idleSearchQuery = currentTypedQuery;

    if (nextIndex === -1) {
      setSelectedIndex(-1);
      setInputQuery(currentTypedQuery);
    } else {
      if (currentIndex === -1) {
        idleSearchQuery = currentTypedQuery || searchQuery;
        if (idleSearchQuery !== currentTypedQuery) {
          setTypedQuery(idleSearchQuery);
        }
      }
      setSelectedIndex(nextIndex);
      setInputQuery(currentResults[nextIndex]);
    }

    scheduleIdleSearch(idleSearchQuery);
  }, [scheduleIdleSearch, searchQuery, setInputQuery]);

  const performSearch = useCallback((query) => {
    if (!query) return;

    clearIdleSearch();
    resetAutocompleteRequest();
    clearPreview();
    setIsFocused(false);
    inputRef.current?.blur();
    setTypedQuery('');
    dispatch(setAutocompleteResults(false));
    dispatch(setHomepageSearchQuery(''));
    AnalyticsManager.logSearchPress(query);
    history.push(`${routes.search_results}/${query}`);
  }, [clearIdleSearch, clearPreview, dispatch, history, resetAutocompleteRequest]);

  const resetSearchState = useCallback(() => {
    clearIdleSearch();
    resetAutocompleteRequest();
    clearPreview();
    setIsFocused(false);
    inputRef.current?.blur();
    setTypedQuery('');
    dispatch(setAutocompleteResults(false));
    dispatch(setHomepageSearchQuery(''));
  }, [clearIdleSearch, clearPreview, dispatch, resetAutocompleteRequest]);

  const handleInput = useCallback((e) => {
    clearPreview();
    const query = e.target.value ;
    setTypedQuery(query);
    setSelectedIndex(-1);
    setInputQuery(query);
    clearIdleSearch();
    resetAutocompleteRequest();

    if (query.trim().length < 3) {
      dispatch(setAutocompleteResults(false));
      return;
    }

    setIsFocused(true);
    fetchAutocomplete(query);
    scheduleIdleSearch(query);
  }, [
    clearIdleSearch,
    clearPreview,
    dispatch,
    fetchAutocomplete,
    resetAutocompleteRequest,
    scheduleIdleSearch,
    setInputQuery,
  ]);

  const handleKeyDown = useCallback((e) => {
    const { selectedIndex: currentIndex, results: currentResults, typedQuery: currentTypedQuery } = latestRef.current;

    switch (e.keyCode) {
      case 40:
        if (currentResults.length === 0) break;
        e.preventDefault();
        if (currentIndex === -1) {
          navigateSelection(0);
        } else if (currentIndex >= currentResults.length - 1) {
          navigateSelection(0);
        } else {
          navigateSelection(currentIndex + 1);
        }
        break;
      case 38:
        if (currentResults.length === 0) break;
        e.preventDefault();
        if (currentIndex === -1) {
          navigateSelection(currentResults.length - 1);
        } else if (currentIndex === 0) {
          navigateSelection(-1);
        } else {
          navigateSelection(currentIndex - 1);
        }
        break;
      case 27:
        if (currentIndex >= 0) {
          e.preventDefault();
          navigateSelection(-1);
        }
        break;
      case 13:
        e.preventDefault();
        if (currentIndex >= 0 && currentResults[currentIndex]) {
          performSearch(currentResults[currentIndex]);
        } else if (currentTypedQuery !== '') {
          performSearch(currentTypedQuery);
        }
        break;
      default:
        break;
    }
  }, [navigateSelection, performSearch]);

  const onFocus = useCallback(() => {
    setIsFocused(true);
    const { typedQuery: currentTypedQuery, autocompleteStatus: status } = latestRef.current;

    if (
      currentTypedQuery.length >= 3
      && (status === AUTOCOMPLETE_STATUS.IDLE || status === AUTOCOMPLETE_STATUS.ERROR)
    ) {
      fetchAutocomplete(currentTypedQuery);
      scheduleIdleSearch(currentTypedQuery);
    }
  }, [fetchAutocomplete, scheduleIdleSearch]);

  const onBlur = useCallback((e) => {
    if (!latestRef.current.isFocused) return;

    const isChild = e.composedPath().indexOf(resultRef.current) !== -1;
    if (e.target !== inputRef.current && !isChild) {
      const { selectedIndex: currentIndex, typedQuery: currentTypedQuery } = latestRef.current;

      if (currentIndex >= 0) {
        setSelectedIndex(-1);
        setInputQuery(currentTypedQuery);
      }
      setIsFocused(false);
    }
  }, [setInputQuery]);

  latestRef.current.isFocused = isFocused;

  const handleClearTextClick = useCallback(() => {
    clearIdleSearch();
    resetAutocompleteRequest();
    clearPreview();
    setTypedQuery('');
    dispatch(setAutocompleteResults(false));
    dispatch(setHomepageSearchQuery(''));
  }, [clearIdleSearch, clearPreview, dispatch, resetAutocompleteRequest]);

  const closePreviewBenefit = useCallback(() => {
    setIsFocused(false);
  }, []);

  const blurInput = useCallback(() => {
    inputRef.current?.blur();
  }, []);

  const unfocus = useCallback(() => {
    setIsFocused(false);
  }, []);

  useEffect(() => {
    setSelectedIndex(-1);
    if (typedQuery !== searchQuery) {
      setInputQuery(typedQuery);
    }
  }, [results]);

  useEffect(() => () => {
    clearTimeout(idleSearchTimeoutRef.current);
    abortControllerRef.current?.abort();
    idleSearchTimeoutRef.current = null;
    abortControllerRef.current = null;
  }, []);

  useEffect(() => {
    document.addEventListener('click', onBlur);
    return () => document.removeEventListener('click', onBlur);
  }, [onBlur]);

  return {
    inputRef,
    resultRef,
    isFocused,
    setIsFocused,
    searchQuery,
    typedQuery,
    selectedIndex,
    results,
    autocompleteStatus,
    previewBenefits,
    previewStatus,
    searchDisplayStatus,
    getMatchQuery,
    handleInput,
    handleKeyDown,
    onFocus,
    performSearch,
    resetSearchState,
    handleClearTextClick,
    closePreviewBenefit,
    blurInput,
    unfocus,
  };
};
